import { Check, Shield } from '@phosphor-icons/react';
import { Entity } from '../../types/apiTypes';
import { useScreenView } from '../../utils/ScreenViewContext';
import { useUser } from '../../utils/UserContext';
import { Tooltip } from 'antd';
import { Fragment, useState } from 'react';
import SignupHeaderLogos from './SignupHeaderLogos';

interface SignupBreadcrumbsProps {
  currStep: string;
  selectedRole: Entity;
}

interface Step {
  label: string;
  value: number;
  displayValue?: number;
  title: string;
  desc: string;
}

const stepsCustomer: Step[] = [
  {
    label: 'Step 1',
    value: 0,
    title: 'Business Info',
    desc: 'A few details about your business to get started.',
  },
  {
    label: 'Step 2',
    value: 1,
    title: 'Owner Info',
    desc: `A few details about yourself.`,
  },
  {
    label: 'Step 3',
    value: 2,
    title: 'Choose Tier',
    desc: 'Select the tier that best fits you and your business.',
  },
  {
    label: 'Step 4',
    value: 3,
    title: 'Review Info',
    desc: 'Review your information before continuing.',
  },
];

const stepsCustomerTwo: Step[] = [
  {
    label: 'Step 6',
    value: 6,
    displayValue: 1,
    title: 'Asset Info',
    desc: 'Add your information about your trucks & trailers.',
  },
];

const stepsVendor: Step[] = [
  {
    label: 'Step 1',
    value: 0,
    title: 'Business Info',
    desc: 'Tell us about your business',
  },
  {
    label: 'Step 2',
    value: 1,
    title: 'Account Info',
    desc: `Fill in in your personal information.`,
  },
  {
    label: 'Step 3',
    value: 2,
    title: 'Additional Contact',
    desc: 'Please add another management contact person at your business.',
  },
];

const stepsPartner: Step[] = [
  {
    label: 'Step 1',
    value: 0,
    title: 'Business Info',
    desc: 'Tell us about your business',
  },
  {
    label: 'Step 2',
    value: 1,
    title: 'Account Info',
    desc: `Fill in in your personal information.`,
  },
  {
    label: 'Step 3',
    value: 2,
    title: 'Additional Contact',
    desc: 'Please add another management contact person at your business.',
  },
  {
    label: 'Step 4',
    value: 3,
    title: 'Contract',
    desc: 'Review and sign your contract.',
  },
];

// Steps with no meaningful breadcrumb to show. The latter-half "secondary" flow now has a single
// step (Asset Info) after Plaid IDV was removed, so we hide breadcrumbs across that half rather
// than render a lone crumb.
const stepsToHideBreadcrumbs = [
  'plaid_link_retry',
  'final_consent',
  'assets',
  'pending_review',
  'plaid_link',
  // The Agreement crumb is dropped (THT-773) — master_service_agreement now presents both
  // agreements with no crumb of its own, and bank_agreement rides the same ordinal.
  'master_service_agreement',
  'bank_agreement',
];

/**
 * The top bar's Log Out control, shared by all three breakpoints so the treatment cannot drift.
 *
 * Mobile and tablet add `-mr-3` at the call site: on their narrow gutters the button's own
 * horizontal padding would push the label a visible 12px further in than the logo sits from the
 * opposite edge, and pulling that padding back over the gutter squares the two up without
 * shrinking the tap target. Desktop's gutter is wide enough that the same offset reads as
 * nothing, so it is left alone.
 */
const LOGOUT_BUTTON_CLASS =
  'rounded-md px-3 py-1.5 text-gray-600 transition-colors hover:bg-gray-100 hover:text-accent';

function SignupBreadcrumbs({ currStep, selectedRole }: SignupBreadcrumbsProps) {
  const { screenView } = useScreenView();
  const { logout } = useUser();
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);

  const stepOrderMapCustomers = new Map<string, number>([
    ['business_info', 0],
    ['personal_info', 1],
    ['choose_plan', 2],
    ['review', 3],
    ['master_service_agreement', 4],
    // Folded into master_service_agreement, which now presents both agreements. Mapped to the
    // same ordinal rather than dropped, so an in-flight application still parked on the old step
    // resolves to the crumb it is actually being shown. Ordinal 5 is now unused (the removed
    // prequal_decision step); assets keeps its 6, since stepsCustomerTwo looks Asset Info up by
    // that value.
    ['bank_agreement', 4],
    ['assets', 6],
  ]);

  const secondaryStepsSetCustomers = new Set([
    'plaid_link',
    'assets',
    'pending_review',
  ]);

  const stepOrderMapVendors = new Map<string, number>([
    ['vendor_business_info', 0],
    ['vendor_personal_info', 1],
    ['vendor_management_contact', 2],
    ['vendor_pending_review', 3],
  ]);

  const stepOrderMapPartners = new Map<string, number>([
    ['partner_business_info', 0],
    ['partner_personal_info', 1],
    ['partner_management_contact', 2],
    ['partner_contract', 3],
    ['partner_pending_review', 4],
  ]);

  const getStepOrderMap = (role: Entity) => {
    if (role === Entity.Customer) {
      return stepOrderMapCustomers;
    } else if (role === Entity.Vendor) {
      return stepOrderMapVendors;
    } else if (role === Entity.Partner) {
      return stepOrderMapPartners;
    }
  };
  const getSteps = (role: Entity) => {
    if (role === Entity.Customer) {
      return secondaryStepsSetCustomers.has(currStep)
        ? stepsCustomerTwo
        : stepsCustomer;
    } else if (role === Entity.Vendor) {
      return stepsVendor;
    } else if (role === Entity.Partner) {
      return stepsPartner;
    }
  };
  const stepOrderMap = getStepOrderMap(selectedRole);

  const renderBreadcrumbs = (role: Entity, screenView: string) => {
    const steps = getSteps(role);

    const renderBreadcrumbScreenView = (screenView: string) => {
      switch (screenView) {
        case 'desktop':
          return (
            <div
              className={`flex h-full w-full ${stepOrderMap?.has(currStep) && (stepOrderMap?.get(currStep) ?? -1) >= 6 && role === Entity.Customer ? 'max-w-[500px]' : 'max-w-[1000px]'} items-center justify-between`}
            >
              {steps?.map((step, index) => {
                const isCompleted =
                  stepOrderMap &&
                  stepOrderMap?.has(currStep) &&
                  (stepOrderMap?.get(currStep) ?? -1) > step?.value;
                const isActive =
                  (stepOrderMap?.get(currStep) ?? -1) === step?.value;

                return (
                  <>
                    <Tooltip title={step?.desc} key={`step-tooltip-${index}`}>
                      <div
                        key={`step-${index}`}
                        className={`flex items-center gap-x-3 transition-all duration-300 ${
                          isActive ? 'scale-105' : ''
                        }`}
                      >
                        {isCompleted ? (
                          <>
                            <div className="relative">
                              <div className="flex h-[36px] w-[36px] min-w-[36px] items-center justify-center rounded-full bg-gradient-to-br from-secondary to-ctadarkgreen p-[3px] text-center align-middle text-white shadow-md transition-all duration-300 hover:shadow-lg">
                                <Check size={22} weight="bold" />
                              </div>
                              <div className="absolute -inset-0.5 animate-pulse-slow rounded-full bg-gradient-to-r from-secondary to-green-600 opacity-30"></div>
                            </div>
                            <p className="whitespace-nowrap text-[15px] font-[600] text-gray-800">
                              {step?.title}
                            </p>
                          </>
                        ) : isActive ? (
                          <>
                            <div className="relative">
                              <div className="flex h-[36px] w-[36px] min-w-[36px] items-center justify-center rounded-full bg-gradient-to-br from-[#6A9E68] to-[#5a8558] p-[3px] text-center align-middle text-white shadow-md">
                                <div className="h-[16px] w-[16px] animate-pulse-slow rounded-full bg-white"></div>
                              </div>
                              <div className="absolute -inset-1 animate-ping-slow rounded-full bg-gradient-to-r from-[#6A9E68] to-[#5a8558] opacity-20"></div>
                            </div>
                            <p className="whitespace-nowrap text-[15px] font-[600] text-primary">
                              {step?.title}
                            </p>
                          </>
                        ) : (
                          <>
                            <div className="flex h-[36px] w-[36px] min-w-[36px] items-center justify-center rounded-full bg-gray-200 p-[3px] align-middle text-gray-600 transition-all duration-300 hover:bg-gray-300">
                              <p className="text-center text-[14px] font-[600]">
                                {step?.displayValue ?? step?.value + 1}
                              </p>
                            </div>
                            <p className="whitespace-nowrap text-[15px] font-[500] text-gray-500">
                              {step?.title}
                            </p>
                          </>
                        )}
                      </div>
                    </Tooltip>

                    {index !== steps?.length - 1 && (
                      <div
                        key={`line-${index}`}
                        className={`${
                          (stepOrderMap?.get(currStep) ?? -1) >= 8
                            ? 'max-w-[100px]'
                            : 'max-w-[80px]'
                        } relative mx-2 h-[2px] flex-1 overflow-hidden ${
                          isCompleted ? 'bg-secondary' : 'bg-gray-300'
                        }`}
                      >
                        {isCompleted && (
                          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                        )}
                      </div>
                    )}
                  </>
                );
              })}
            </div>
          );
        case 'tablet':
          // One column per step — circle and title in the SAME flex item. They used to be two
          // independent centred rows, which meant a title only sat under its own circle by
          // coincidence: the circles spread across a 552px track while the titles were fixed
          // 105px boxes, so the two rows drifted apart at the ends. That fixed width also
          // clipped the longer titles ("Service Agreement" rendered as "Service…"), hence
          // line-clamp-2 here — a title that needs two lines gets them.
          return (
            <div className="flex w-full max-w-[640px] flex-row items-start justify-center">
              {steps?.map((step, index) => {
                const isCompleted =
                  stepOrderMap &&
                  stepOrderMap?.has(currStep) &&
                  (stepOrderMap?.get(currStep) ?? -1) > step?.value;
                const isActive =
                  (stepOrderMap?.get(currStep) ?? -1) === step?.value;

                return (
                  <Fragment key={`step-tablet-${index}`}>
                    <Tooltip title={step?.desc}>
                      <div
                        className={`flex w-[88px] shrink-0 flex-col items-center gap-y-2 transition-all duration-300 ${
                          isActive ? 'scale-105' : ''
                        }`}
                      >
                        {isCompleted ? (
                          <div className="relative">
                            <div className="flex h-[34px] w-[34px] min-w-[34px] items-center justify-center rounded-full bg-gradient-to-br from-secondary to-ctadarkgreen p-[3px] text-center align-middle text-white shadow-md transition-all duration-300 hover:shadow-lg">
                              <Check size={20} weight="bold" />
                            </div>
                            <div className="absolute -inset-0.5 animate-pulse-slow rounded-full bg-gradient-to-r from-secondary to-green-600 opacity-30"></div>
                          </div>
                        ) : isActive ? (
                          <div className="relative">
                            <div className="flex h-[34px] w-[34px] min-w-[34px] items-center justify-center rounded-full bg-gradient-to-br from-[#6A9E68] to-[#5a8558] p-[3px] text-center align-middle text-white shadow-md">
                              <div className="h-[14px] w-[14px] animate-pulse-slow rounded-full bg-white"></div>
                            </div>
                            <div className="absolute -inset-1 animate-ping-slow rounded-full bg-gradient-to-r from-[#6A9E68] to-[#5a8558] opacity-20"></div>
                          </div>
                        ) : (
                          <div className="flex h-[34px] w-[34px] min-w-[34px] items-center justify-center rounded-full bg-gray-200 p-[3px] align-middle text-gray-600 transition-all duration-300 hover:bg-gray-300">
                            <p className="text-center text-[14px] font-[600]">
                              {step?.displayValue ?? step?.value + 1}
                            </p>
                          </div>
                        )}
                        <p
                          className={`line-clamp-2 text-center text-[13px] leading-tight transition-colors duration-300 ${
                            isCompleted
                              ? 'font-[600] text-gray-800'
                              : isActive
                                ? 'font-[600] text-primary'
                                : 'font-[500] text-gray-500'
                          }`}
                        >
                          {step?.title}
                        </p>
                      </div>
                    </Tooltip>
                    {index !== steps?.length - 1 && (
                      // mt-[16px] parks the connector on the circles' centre line (34px tall,
                      // 2px rule) now that it is a sibling of a full column rather than a circle.
                      <div
                        className={`relative mx-1 mt-[16px] h-[2px] min-w-[12px] max-w-[48px] flex-1 overflow-hidden ${
                          isCompleted ? 'bg-secondary' : 'bg-gray-300'
                        }`}
                      >
                        {isCompleted && (
                          <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                        )}
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </div>
          );
        case 'mobile': {
          // Use narrower max-width for fewer steps
          const maxWidth =
            steps && steps.length <= 3 ? 'max-w-[100px]' : 'max-w-[280px]';
          return (
            <div className={`flex w-full ${maxWidth} flex-row items-center`}>
              {steps?.map((step, index) => {
                const isCompleted =
                  stepOrderMap &&
                  stepOrderMap?.has(currStep) &&
                  (stepOrderMap?.get(currStep) ?? -1) > step?.value;
                const isActive =
                  (stepOrderMap?.get(currStep) ?? -1) === step?.value;

                return (
                  <>
                    <div key={`step-mobile-${index}`} className="flex-shrink-0">
                      {isCompleted ? (
                        <div className="flex h-[24px] w-[24px] items-center justify-center rounded-full bg-gradient-to-br from-secondary to-ctadarkgreen text-white shadow-sm">
                          <Check size={14} weight="bold" />
                        </div>
                      ) : isActive ? (
                        <div className="flex h-[24px] w-[24px] items-center justify-center rounded-full bg-gradient-to-br from-[#6A9E68] to-[#5a8558] text-white shadow-sm">
                          <div className="h-[10px] w-[10px] animate-pulse-slow rounded-full bg-white"></div>
                        </div>
                      ) : (
                        <div className="flex h-[24px] w-[24px] items-center justify-center rounded-full bg-gray-200 text-gray-600">
                          <p className="text-center text-[11px] font-[600]">
                            {step?.displayValue ?? step?.value + 1}
                          </p>
                        </div>
                      )}
                    </div>
                    {index !== steps?.length - 1 && (
                      <div
                        key={`line-mobile-${index}`}
                        className={`h-[2px] w-full min-w-[20px] ${
                          isCompleted ? 'bg-secondary' : 'bg-gray-300'
                        }`}
                      />
                    )}
                  </>
                );
              })}
            </div>
          );
        }
      }
    };

    return <>{renderBreadcrumbScreenView(screenView)}</>;
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <>
            {stepOrderMap?.has(currStep) ? (
              <div className="relative bg-gradient-to-b from-white to-gray-50 shadow-sm">
                <div className="flex h-auto w-full flex-col py-6">
                  <div className="flex w-full items-center justify-between gap-6 px-12">
                    <div className="shrink-0">
                      <SignupHeaderLogos size="regular" />
                    </div>
                    {/* Steps */}
                    {!stepsToHideBreadcrumbs.includes(currStep) && (
                      <div className="flex min-w-0 flex-1 items-center justify-center">
                        {renderBreadcrumbs(selectedRole, screenView)}
                      </div>
                    )}
                    <div className="flex shrink-0 items-center gap-4">
                      <div className="xl:flex hidden items-center gap-2 text-sm text-gray-600">
                        <Shield size={16} className="text-green-600" />
                        <span className="font-medium">Secure Application</span>
                      </div>
                      <button
                        className={LOGOUT_BUTTON_CLASS}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setShowLogoutConfirmation(true);
                        }}
                      >
                        Log Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="relative bg-gradient-to-b from-white to-gray-50 shadow-sm">
                <div className="flex h-[100px] items-center justify-between px-12">
                  <SignupHeaderLogos size="regular" />
                  <button
                    className={LOGOUT_BUTTON_CLASS}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setShowLogoutConfirmation(true);
                    }}
                  >
                    Log Out
                  </button>
                </div>
              </div>
            )}
          </>
        );
      case 'tablet':
        return (
          <>
            {stepOrderMap?.has(currStep) ? (
              // px-6 rather than the old px-12: at the 600px low end of the tablet range a 48px
              // gutter left the breadcrumb track too little room and it overflowed the bar.
              <div className="relative flex w-full flex-col items-center justify-center gap-y-[20px] bg-gradient-to-b from-white to-gray-50 px-6 py-[20px] align-middle shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <SignupHeaderLogos size="compact" />
                  <div className="flex shrink-0 items-center gap-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Shield size={16} className="text-green-600" />
                      <span className="font-medium">Secure Application</span>
                    </div>
                    <button
                      className={`-mr-3 ${LOGOUT_BUTTON_CLASS}`}
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setShowLogoutConfirmation(true);
                      }}
                    >
                      Log Out
                    </button>
                  </div>
                </div>
                {!stepsToHideBreadcrumbs.includes(currStep) &&
                  renderBreadcrumbs(selectedRole, screenView)}
              </div>
            ) : (
              // The same bar as above minus the breadcrumbs. The steps that land here are still
              // mid-signup, so the chrome must not change under the customer: this used to be a
              // centred logo with Log Out absolutely positioned against the top edge, which put
              // the control out of line with the logo and hard against the viewport edge.
              <div className="relative flex w-full items-center justify-between bg-gradient-to-b from-white to-gray-50 px-6 py-[20px] shadow-sm">
                <SignupHeaderLogos size="compact" />
                <button
                  className={`-mr-3 shrink-0 ${LOGOUT_BUTTON_CLASS}`}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setShowLogoutConfirmation(true);
                  }}
                >
                  Log Out
                </button>
              </div>
            )}
          </>
        );
      case 'mobile':
        return (
          <>
            {stepOrderMap?.has(currStep) ? (
              // px-4 rather than the old p-2: an 8px gutter on a 375px screen left the logo and
              // the Log Out control all but touching the edges of the phone.
              <div className="relative flex w-full flex-col items-center justify-center gap-y-[20px] bg-gradient-to-b from-white to-gray-50 px-4 py-3 align-middle shadow-sm">
                <div className="flex w-full items-center justify-between">
                  <SignupHeaderLogos size="compact" />
                  <button
                    className={`-mr-3 shrink-0 ${LOGOUT_BUTTON_CLASS}`}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setShowLogoutConfirmation(true);
                    }}
                  >
                    Log Out
                  </button>
                </div>
                {!stepsToHideBreadcrumbs.includes(currStep) &&
                  renderBreadcrumbs(selectedRole, screenView)}
                {!stepsToHideBreadcrumbs.includes(currStep) && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Shield size={16} className="text-green-600" />
                    <span className="font-medium">Secure Application</span>
                  </div>
                )}
              </div>
            ) : (
              // The same bar as above minus the breadcrumbs. Log Out was previously pinned to
              // `right-0 top-0` of a padding-less container, which ran the label off the right
              // edge of the phone — the state this fix was reported from.
              <div className="relative flex w-full items-center justify-between bg-gradient-to-b from-white to-gray-50 px-4 py-3 shadow-sm">
                <SignupHeaderLogos size="compact" />
                <button
                  className={`-mr-3 shrink-0 ${LOGOUT_BUTTON_CLASS}`}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setShowLogoutConfirmation(true);
                  }}
                >
                  Log Out
                </button>
              </div>
            )}
          </>
        );
    }
  };

  const LogoutConfirmationModal = () => {
    if (!showLogoutConfirmation) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <h2 className="mb-4 text-xl font-bold text-gray-900">
            Confirm Logout
          </h2>
          <p className="mb-6 text-gray-600">
            Are you sure you want to log out? Your progress has been saved.
          </p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowLogoutConfirmation(false)}
              className="rounded-md border border-gray-300 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowLogoutConfirmation(false);
                logout();
              }}
              className="rounded-md bg-red-500 px-4 py-2 text-white transition-colors hover:bg-red-600"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {renderScreenView(screenView)}
      <LogoutConfirmationModal />
    </>
  );
}

export default SignupBreadcrumbs;
